import type { TeactNode } from '../lib/teact/teact';

import type {
  AgentAnswerFeedbackV2,
  AgentAnswerLinkV1,
  AgentAnswerTableReferenceV1,
  AgentAnswerTableV1,
  AgentThreadNewsCardV2,
} from '../api/agentV2/protocol/types';
import type {
  AgentPersistedActionV2,
  AgentPublicFollowUpV2,
  AgentResponseLanguageV1,
  AgentSemanticContentV1,
  AgentV2LiveAction,
  AgentV2MessageError,
} from '../api/agentV2/protocol/types';
import type {
  AgentV2ActionPresentation,
} from '../api/agentV2/types';
import type { ApiTonWalletVersion } from '../api/chains/ton/types';
import type { TonConnectProof } from '../api/dappProtocols/adapters';
import type {
  WcPayMerchant,
  WcPayPaymentAmount,
  WcPayPaymentInfo,
  WcPayPaymentOption,
} from '../api/dappProtocols/adapters/walletConnect/types';
import type { ApiParsedSignDataCellPreview } from '../api/dappProtocols/signDataCellPreview';
import type { StoredDappConnection } from '../api/dappProtocols/storage';
import type { UnifiedSignDataPayload } from '../api/dappProtocols/types';
import type {
  ApiAccountConfig,
  ApiActivity,
  ApiAnyDisplayError,
  ApiBackendConfig,
  ApiBalanceBySlug,
  ApiBaseCurrency,
  ApiChain,
  ApiCountryCode,
  ApiCurrencyRates,
  ApiDappPermissions,
  ApiDappTransfer,
  ApiDerivation,
  ApiEmulationResult,
  ApiFetchEstimateDieselResult,
  ApiForeignStakingPromotion,
  ApiForeignUnstakeDraft,
  ApiGroupedWalletVariant,
  ApiHistoryList,
  ApiImportAddressByChain,
  ApiInfoBannerPromotion,
  ApiLedgerDriver,
  ApiLedgerWalletInfo,
  ApiMarketAssetsResponseWithSlug,
  ApiMtwCardType,
  ApiNetwork,
  ApiNft,
  ApiNftCollection,
  ApiPerpsAssetDetail,
  ApiPerpsAssetId,
  ApiPerpsGetAssetsResult,
  ApiPerpsListedAsset,
  ApiPerpsOperation,
  ApiPortfolioHistoryResponse,
  ApiPriceHistoryBackfill,
  ApiPriceHistoryPeriod,
  ApiSite,
  ApiSiteCategory,
  ApiStakingHistory,
  ApiStakingState,
  ApiSwapAsset,
  ApiSwapCexLabel,
  ApiSwapDexLabel,
  ApiSwapDexRouterLabel,
  ApiSwapHint,
  ApiSwapRoute,
  ApiSwapRouteType,
  ApiSwapVersion,
  ApiTokenDetails,
  ApiTokenType,
  ApiTokenWithPrice,
  ApiUpdate,
  ApiUpdateDappCloseLoading,
  ApiUpdateDappConnect,
  ApiUpdateDappLoading,
  ApiUpdateDappSendTransactions,
  ApiUpdateDappSignData,
  ApiUpdateWalletConnectPayPaymentComplete,
  ApiUpdateWalletConnectPayProcessing,
  ApiUpdateWalletConnectPaySignData,
  ApiUpdateWalletConnectPaySignTransaction,
  ApiUpdateWalletVersions,
  ApiVestingInfo,
  ApiWalletWithVersionInfo,
  NativePlatform,
} from '../api/types';
import type { AUTOLOCK_OPTIONS_LIST } from '../config';
import type { LegacyAuthConfig } from '../enclave';
import type { ExplainedTransferFee } from '../util/fee/transferFee';
import type { LedgerTransport } from '../util/ledger/types';

export { SwapType } from '../util/swap/types';

export type IAnchorPosition = {
  x: number;
  y: number;
};

export type PortfolioHistoryBundle = {
  netWorth?: ApiPortfolioHistoryResponse;
  pnlCumulative?: ApiPortfolioHistoryResponse;
  pnl?: ApiPortfolioHistoryResponse;
  // Precomputed P&L change for this range+currency, kept here (not just in the single-slot
  // `pnlChangeByAccountId`) so switching back to a cached range shows the right value instantly
  pnlChange?: PortfolioPnlChange;
  // Quantized timestamp of the fetch (see `getPortfolioHistorySlot`); when the current slot
  // still matches this value, the bundle is considered fresh and no network call is issued
  fetchedAtSlot?: number;
};

type PortfolioHistoryByRange = Record<ApiPriceHistoryPeriod, PortfolioHistoryBundle>;
type PortfolioHistoryByBaseCurrency = Record<ApiBaseCurrency, PortfolioHistoryByRange>;
export type PortfolioHistoryByAccountId = Record<string, PortfolioHistoryByBaseCurrency>;
export type PortfolioPnlChange = {
  range: ApiPriceHistoryPeriod;
  baseCurrency: ApiBaseCurrency;
  amount: number;
  percent?: number;
  startTs?: number;
  endTs?: number;
};

export type PortfolioState = {
  historyByAccountId?: PortfolioHistoryByAccountId;
  pnlChangeByAccountId?: Record<string, PortfolioPnlChange>;
  activeRange?: ApiPriceHistoryPeriod;
  isLoading?: boolean;
  isRefreshing?: boolean;
  error?: string;
};

export type AnimationLevel = 0 | 1 | 2;
export type Theme = 'light' | 'dark' | 'system';
export type AppTheme = 'dark' | 'light';
export type AppLayout = 'portrait' | 'landscape';
export type DialogAction = 'openReturnUrl' | 'exportLogs';
export type ToastAction = 'openRenameWallet';
// Why the running bundle needs a reload: the host serves another build, or a chunk failed to load from this one
export type AppReloadReason = 'buildOutdated' | 'chunkLoadFailed';

export type DeveloperSettingsUndefinedOverride = '__undefined';
export type DeveloperSettingsOverrideValue<Value> = Exclude<Value, undefined> | DeveloperSettingsUndefinedOverride;

export interface DeveloperSettingsOverrides {
  seasonalTheme?: DeveloperSettingsOverrideValue<ApiBackendConfig['seasonalTheme']>;
  isGramDiamondEnabled?: true;
}

export type DeveloperSettingsOverrideKey = keyof DeveloperSettingsOverrides;
export type DeveloperSettingsOverridePayload = {
  [Key in DeveloperSettingsOverrideKey]: {
    key: Key;
    value?: DeveloperSettingsOverrides[Key];
  };
}[DeveloperSettingsOverrideKey];

export type ToastType = {
  icon?: string;
  message: string;
} & (
  | { action?: undefined; actionText?: undefined }
  | { action: ToastAction; actionText: string }
);

export type DialogType = {
  title?: string;
  message: string | TeactNode;
  entities?: Record<string, any>;
  noBackdropClose?: boolean;
  isInAppLock?: boolean;
  buttons?: {
    confirm: { title?: string; action?: DialogAction; isDestructive?: boolean };
    cancel?: { title?: string };
  };
};

/**
 * How a stopped legacy migration is put to the user. The inline form belongs under the input, next to
 * the attempt that can be repeated; the dialog form answers a failure the retry cannot help with, and
 * carries a code for support when the app could not establish a cause.
 */
export type MigrationErrorPresentation =
  /** Nothing to say: the person stopped the migration themselves, and the screen keeps its retry */
  | { kind: 'silent' }
  | { kind: 'inline'; text: string }
  | { kind: 'dialog'; titleKey: string; messageKey: string; errorCode?: string };

export type LangCode = 'en' | 'es' | 'ru' | 'zh-Hant' | 'zh-Hans' | 'tr' | 'de' | 'th' | 'uk' | 'pl' | 'ar' | 'fa'
  | 'it';
export type LanguageSource = 'system' | 'user';

export interface LangItem {
  langCode: LangCode;
  name: string;
  nativeName: string;
  rtl: boolean;
}

export interface LangString {
  zeroValue?: string;
  oneValue?: string;
  twoValue?: string;
  fewValue?: string;
  manyValue?: string;
  otherValue?: string;
}

export type LangPack = Record<string, string | LangString>;

export type StakingStatus = 'active' | 'unstakeRequested';

export type AuthMethod = 'createAccount' | 'importMnemonic' | 'importHardwareWallet';

interface AuthAccount {
  accountId: string;
  byChain: Partial<Record<ApiChain, AccountChain>>;
  network?: ApiNetwork;
  partial?: Partial<Account>;
}

type SignOutLevel = 'account' | 'network' | 'all';

export enum AppState {
  Auth,
  Main,
  Market,
  Agent,
  Explore,
  Portfolio,
  TokenInfo,
  Settings,
  Ledger,
  Inactive,
  Empty,
}

export enum AuthState {
  none,
  createWallet,
  checkPassword,
  createPin,
  confirmPin,
  createBiometrics,
  createPassword,
  importWalletCheckPassword,
  importWallet,
  importWalletCreatePin,
  importWalletConfirmPin,
  importWalletCreateBiometrics,
  importWalletCreatePassword,
  about,
  safetyRules,
  mnemonicPage,
  checkWords,
  importViewAccount,
  congratulations,
  importCongratulations,
  ready,
}

export type AuthType = 'passcode' | 'biometric';

export interface EnclaveSession {
  token: string;
  validUntil?: number;
}

export enum AccountSelectorState {
  Cards,
  List,
  Reorder,
  AddAccountInitial,
  AddAccountPassword,
  AddAccountConnectHardware,
  AddAccountSelectHardware,
  AddAccountViewMode,
}

export enum BiometricsState {
  None,
  TurnOnRegistration,
  TurnOnVerification,
  TurnOnComplete,
  TurnOffWarning,
  TurnOffCreatePassword,
  TurnOffComplete,
}

export enum TransferState {
  None,
  Initial,
  Confirm,
  Password,
  ConnectHardware,
  ConfirmHardware,
  ConfirmMfa,
  Complete,
  SelectAccount,
}

export enum RemoveMfaState {
  None,
  Confirm,
  Password,
  ConfirmMfa,
  Complete,
}

export const enum TransactionInfoState {
  None,
  Loading,
  ActivityList,
  ActivityDetail,
}

export const enum ScamWarningType {
  SeedPhrase = 1,
  DomainLike,
}

export enum SignDataState {
  None,
  Initial,
  Password,
}

export enum WalletConnectPayState {
  None,
  Initial,
  Password,
  Processing,
  Complete,
}

export enum DomainRenewalState {
  None,
  Initial,
  Password,
  ConnectHardware,
  ConfirmHardware,
  ConfirmMfa,
  Complete,
}

export enum DomainLinkingState {
  None,
  Initial,
  Password,
  ConnectHardware,
  ConfirmHardware,
  ConfirmMfa,
  Complete,
}

export enum SwapState {
  None,
  Initial,
  Blockchain,
  Password,
  WaitTokens,
  ConfirmMfa,
  Complete,
  SelectTokenFrom,
  SelectTokenTo,
  SelectAccount,
}

export enum SwapInputSource {
  In,
  Out,
}

export type TradeDirection = 'buy' | 'sell';

export type OnRampWidgetModalParams = {
  chain: ApiChain;
  provider?: 'moonpay' | 'avanchange';
  currency?: ApiBaseCurrency;
};

export type OffRampWidgetModalParams = {
  chain: ApiChain;
  currency?: ApiBaseCurrency;
  /** The amount to sell, in the native token. The whole transferable balance is sold when absent */
  amount?: string;
};

export enum SwapErrorType {
  UnexpectedError,
  InvalidPair,
  NotEnoughLiquidity,

  ChangellyMinSwap,
  ChangellyMaxSwap,
  NotEnoughForFee,
  TooSmallAmount,
  QuoteUnavailable,
  ServiceUnavailable,
}

export enum DappConnectState {
  Info,
  SelectAccount,
  Password,
  ConnectHardware,
  ConfirmHardware,
  AddAccountPassword,
}

export enum HardwareConnectState {
  Connect,
  Connecting,
  Failed,
  Connected,
  WaitingForRemoteTab,
}

export enum StakingState {
  None,

  StakeInitial,
  StakePassword,
  StakeConnectHardware,
  StakeConfirmHardware,
  StakeConfirmMfa,
  StakeComplete,

  UnstakeInitial,
  UnstakePassword,
  UnstakeConnectHardware,
  UnstakeConfirmHardware,
  UnstakeConfirmMfa,
  UnstakeComplete,

  ClaimPassword,
  ClaimConnectHardware,
  ClaimConfirmHardware,
  ClaimConfirmMfa,
  ClaimComplete,

  StakeSelectAccount,
  UnstakeSelectAccount,
  ClaimSelectAccount,

  ForeignUnstakePassword,
  ForeignUnstakeConnectHardware,
  ForeignUnstakeConfirmHardware,
  ForeignUnstakeConfirmMfa,
}

export enum VestingUnfreezeState {
  Password,
  ConnectHardware,
  ConfirmHardware,
}

export enum SettingsState {
  Initial,
  PushNotifications,
  Appearance,
  Assets,
  Security,
  Dapps,
  Language,
  About,
  Disclaimer,
  SelectTokenList,
  WalletVariants, // Wallet Derivations
  WalletVersions, // TON Versions
  LedgerConnectHardware,
  LedgerSelectWallets,
  HiddenNfts,
  BackupWallet,
  Permissions,
  Chains,
  Developer,
}

export enum MintCardState {
  Initial,
  Password,
  ConnectHardware,
  ConfirmHardware,
  Done,
}

export enum ContentTab {
  Overview,
  Assets,
  Activity,
  Agent,
  Explore,
  Nft,
  Settings,
  Portfolio,
  Market,
}

export enum MediaType {
  Nft,
  /** The pictures of the Agent's News, which the viewer is given when it opens */
  AgentNews,
}

/** The news a picture of the Agent's News belongs to, which the media viewer captions it with and offers actions on */
export interface MediaViewerPictureNews
  extends Pick<AgentThreadNewsCardV2, 'headline' | 'body' | 'sources' | 'source'> {
  /** When the news came, as its card or post dates it */
  timestamp: number;
}

interface MediaViewerPicture {
  url: string;
  title: string;
  news: MediaViewerPictureNews;
}

/** The pictures the media viewer pages through, in their order, when they are not the account's NFTs */
export interface MediaViewerPictures {
  ids: string[];
  byId: Record<string, MediaViewerPicture>;
}

/** Section of the "Hidden NFTs" settings screen the media viewer was opened from */
export type HiddenNftsSection = 'user' | 'scam' | 'unverified';

export type UserToken = {
  amount: bigint;
  name: string;
  localizedName?: string;
  symbol: string;
  image?: string;
  slug: string;
  price: number;
  priceUsd: number;
  decimals: number;
  change24h: number;
  chain: ApiChain;
  tokenAddress?: string;
  isDisabled?: boolean;
  canSwap?: boolean;
  keywords?: string[];
  cmcSlug?: string;
  totalValue: string;
  type?: ApiTokenType;
  color?: string;
  codeHash?: string;
  /** A small dim label to show in the UI right after the token name */
  label?: string;
  /** True if this is a staking token (created from ApiStakingState) */
  isStaking?: boolean;
  stakingId?: string;
};

export type UserSwapToken = Omit<UserToken, 'change24h' | 'chain'> & {
  chain: ApiChain | (string & {});
  isPopular: boolean;
};

export type TokenPeriod = '1D' | '7D' | '1M' | '3M' | '1Y' | 'ALL';

export type TokenChartMode = 'price' | 'netWorth';

export type PriceHistoryPeriods = Partial<Record<ApiPriceHistoryPeriod, ApiHistoryList>>;
export type PriceHistoryBackfillPeriods = Partial<Record<ApiPriceHistoryPeriod, ApiPriceHistoryBackfill>>;

/** Absent while the first request is in flight; an entry with neither `data` nor `hasError` means there is no info */
export type TokenDetailsState = {
  data?: ApiTokenDetails;
  hasError?: true;
  /** The language of the last successful response, as the backend localizes the description */
  langCode?: LangCode;
  /** The time of the last successful response, in milliseconds */
  fetchedAt?: number;
};

export type DieselStatus = 'not-available' | 'not-authorized' | 'pending-previous' | 'available' | 'stars-fee';

export type AccountType = 'mnemonic' | 'hardware' | 'view';

export interface AccountChain {
  address: string;
  domain?: string;
  isMultisig?: true;
  derivation?: ApiDerivation;
  /** Is set only in hardware accounts */
  ledgerIndex?: number;
  /** Is set only in TON wallets */
  version?: ApiTonWalletVersion;
  mfa?: {
    address: string;
    user?: {
      name: string;
      username?: string;
      avatarUrl?: string;
    };
  };
}

export interface Account {
  title?: string;
  type: AccountType;
  byChain: Partial<Record<ApiChain, AccountChain>>;
  isTemporary?: true;
  isPrivateKeyBased?: true;
  /** The stored secret of this wallet could not be read during the Enclave migration, so signing is impossible */
  isRecoveryRequired?: true;
}

export type AssetPairs = Record<string, {
  isReverseProhibited?: boolean;
}>;

export interface AgentMessage {
  tables?: AgentAnswerTableV1[];
  tableReferences?: AgentAnswerTableReferenceV1[];
  /** Links over labels of `text`, which arrive before the text that carries them */
  links?: AgentAnswerLinkV1[];
  id: number;
  text: string;
  shouldCommitMarkdownTail?: boolean;
  isOutgoing: boolean;
  isNewsPost?: boolean;
  timestamp: number;
  isTyping?: boolean;
  isStreaming?: boolean;
  responseLanguage?: AgentResponseLanguageV1;
  semanticContent?: AgentSemanticContentV1;
  actions?: Array<AgentV2LiveAction | AgentPersistedActionV2>;
  actionPresentations?: Record<string, AgentV2ActionPresentation>;
  followups?: AgentPublicFollowUpV2[];
  error?: AgentV2MessageError;
  /** A failure of the chat itself rather than of an answer, in words of its own, as a history that did not load */
  failureText?: string;
  isRetryAvailable?: boolean;
  /** The server stores the answer as complete, so the user can rate it */
  isComplete?: boolean;
  feedback?: AgentAnswerFeedbackV2;
}

export interface AgentHint {
  id: string;
  langCode: LangCode;
  title: string;
  subtitle: string;
  prompt: string;
}

export type PerpsAssetDetailState = {
  data?: ApiPerpsAssetDetail;
  hasError?: boolean;
  fetchedAt?: number;
};

export type GlobalPerpsAssets = ApiPerpsGetAssetsResult & {
  byId: Record<ApiPerpsAssetId, ApiPerpsListedAsset>;
  langCode?: string;
  fetchedAt: number;
};

/** Latest balance-deposit progress. Not written to the cache. */
export type PerpsBalanceDepositProgress = {
  phase: 'submitted' | 'complete' | 'failed';
  depositTxHash?: string;
  status?: string;
  error?: string;
  at: number;
};

export interface AccountState {
  balances?: {
    bySlug: ApiBalanceBySlug;
  };
  activities?: {
    byId: Record<string, ApiActivity>;
    /**
     * The array values are sorted by the activity type (newest to oldest).
     * Undefined means that the activities haven't been loaded, [] means that there are no activities.
     */
    idsMain?: string[];
    /** The record values follow the same rules as `idsMain` */
    idsBySlug?: Record<string, string[]>;
    newestActivitiesBySlug?: Record<string, ApiActivity>;
    isMainHistoryEndReached?: boolean;
    isHistoryEndReachedBySlug?: Record<string, boolean>;
    /** True when the last main-feed history request failed and brought no activities */
    hasMainHistoryError?: boolean;
    /** The record values follow the same rules as `hasMainHistoryError` */
    hasHistoryErrorBySlug?: Record<string, boolean>;
    localActivityIds?: string[];
    /** Doesn't include the local activities */
    pendingActivityIds?: Partial<Record<ApiChain, string[]>>;
    /**
     * May be false when the actual activities are actually loaded (when the app has been loaded from the cache).
     * The initial activities should be considered loaded if `idsMain` is not undefined.
     */
    areInitialActivitiesLoaded?: Partial<Record<ApiChain, boolean>>;
    /**
     * Per-chain main-feed ids. Populated on initial load and grown by `addPastActivities` so the
     * pagination boundary can be recomputed when any chain advances.
     */
    mainActivityIdsByChain?: Partial<Record<ApiChain, string[]>>;
    mainHistoryHasMoreByChain?: Partial<Record<ApiChain, boolean>>;
    /** SDK-provided aliases used only to preserve presentation identity across activity id replacements. */
    activityIdReplacements?: Record<string, string>;
  };
  nfts?: {
    byAddress?: Record<string, ApiNft>;
    orderedAddresses?: string[];
    currentCollection?: ApiNftCollection;
    selectedNfts?: ApiNft[];
    dnsExpiration?: Record<string, number>;
    linkedAddressByAddress?: Record<string, string>;
    collectionTabs?: ApiNftCollection[];
    wasTelegramGiftsAutoAdded?: boolean;
    isLoadedByAddress?: Record<string, true>;
    isFullLoadingByChain?: Partial<Record<ApiChain, boolean>>;
    isFullLoadCompleteByChain?: Partial<Record<ApiChain, boolean>>;
    /** Collection address -> last loaded timestamp for cache TTL */
    collectionLoadedTimestamps?: Record<string, number>;
    /** Snapshot of MW card NFT addresses currently owned by this account */
    ownedMwCardAddresses?: string[];
  };
  blacklistedNftAddresses?: string[];
  whitelistedNftAddresses?: string[];
  selectedNftsToHide?: {
    addresses: string[];
    isCollection: boolean;
  };
  isUnhideNftModalOpen?: boolean;
  selectedNftToUnhide?: {
    address: ApiNft['address'];
    name: ApiNft['name'];
  };
  selectedNftToReport?: {
    chain: ApiChain;
    address: ApiNft['address'];
    shouldReport?: true;
  };
  currentNftForAttributes?: ApiNft;
  shouldShowOwnerInNftAttributes?: true;
  dappLastOpenedDatesByUrl?: Record<string, number>;
  isBackupRequired?: boolean;
  currentTokenSlug?: string;
  currentActivityId?: string;
  currentTokenPeriod?: TokenPeriod;
  /** Catalogue id of the perp whose page is open. Detail and chart load for this id. */
  currentPerpsAssetId?: string;
  currentPerpsAssetPeriod?: ApiPriceHistoryPeriod;
  perpsAssetDetailById?: Record<string, PerpsAssetDetailState>;
  perpsOperations?: Record<string, ApiPerpsOperation>;
  perpsBalanceDeposit?: PerpsBalanceDepositProgress;
  tokenNetWorthHistory?: Record<string, PriceHistoryPeriods>;
  savedAddresses?: SavedAddress[];
  activeContentTab?: ContentTab;
  activityReturnContentTab?: ContentTab;
  activitiesUpdateStartedAt?: number;
  balanceUpdateStartedAt?: number;

  // Staking
  staking?: {
    stakingId?: string;
    stateById?: Record<string, ApiStakingState>;
    totalProfit?: bigint;
    shouldUseNominators?: boolean;
  };

  vesting?: {
    info: ApiVestingInfo[];
    isLoading?: boolean;
    isConfirmRequested?: boolean;
    error?: string;
    unfreezeRequestedIds?: { id: number; partId: number }[];
    unfreezeState?: VestingUnfreezeState;
  };

  stakingHistory?: ApiStakingHistory;
  browserHistory?: string[];

  isDieselAuthorizationStarted?: boolean;
  isLongUnstakeRequested?: boolean;
  isCardMinting?: boolean;
  receiveModalChain?: ApiChain;
  invoiceTokenSlug?: string;
  /** The counter token the user picked by hand on the Buy / Sell screen, by direction */
  tradeCounterTokenSlugs?: Partial<Record<TradeDirection, string>>;
  lastSwapPairsBySlug?: Record<string, { tokenInSlug: string; tokenOutSlug: string }>;

  dapps?: StoredDappConnection[];
  currentSiteCategoryId?: number;

  config?: ApiAccountConfig;
  banners?: {
    home: ApiInfoBannerPromotion[];
    staking: ApiForeignStakingPromotion[];
  };
  // The backend keeps reporting a foreign staking position for up to 15 minutes after the unstake is sent.
  // Its banner stays hidden until the SDK stops sending it, so the user can't send a second unstake.
  submittedForeignStakingIds?: string[];
  isAppReady?: boolean;
}

export type ChainDisplayMode = 'value' | 'manual';

/**
 * How the Blockchains settings screen displays the account's chains.
 *
 * The iOS and Android apps read and write the same value under
 * `settings.byAccountId.<accountId>.chainDisplayConfiguration`, so keep these field names and shapes in sync
 * with `MChainDisplayConfiguration` in the `mobile` folder.
 *
 * `displayMode` - `value` lets the app order and hide the chains by balance; `manual` lets the user do it.
 * `hiddenChains` - chains the user turned off, even though the app would show them.
 * `shownChains` - chains the user turned on, even though the app would hide them.
 * `manualOrder` - the shown chains, in the order the user dragged them into.
 *
 * Only the user's differences from the app's own choice are stored, so a chain funded later appears on its own.
 */
export interface ChainDisplayConfiguration {
  displayMode: ChainDisplayMode;
  hiddenChains?: ApiChain[];
  shownChains?: ApiChain[];
  manualOrder?: ApiChain[];
}

export interface AccountSettings {
  pinnedSlugs?: string[];
  alwaysShownSlugs?: string[];
  alwaysHiddenSlugs?: string[];
  deletedSlugs?: string[];
  importedSlugs?: string[];
  chainDisplayConfiguration?: ChainDisplayConfiguration;
  // These NFTs should be saved in the settings for immediate use after launching the application,
  // without synchronizing the wallet history or complex state caching
  cardBackgroundNft?: ApiNft;
  accentColorNft?: ApiNft;
  accentColorIndex?: number;
  isAllowSuspiciousActions?: boolean;
  areAssetsHidden?: boolean;
  areCollectiblesHidden?: boolean;
  overviewCellSize?: OverviewCellSize;
  dismissedHomeBannerIds?: string[];
}

export type OverviewCellSize = 'small' | 'medium' | 'big';

export interface ForeignUnstake {
  accountId: string;
  bannerId: string;
  chain: ApiChain;
  providerName: string;
  draft?: ApiForeignUnstakeDraft;
}

export interface SavedAddress {
  name: string;
  address: string;
  chain: ApiChain;
}

export interface AddressBookItemData {
  name: string;
  address: string;
  chain: ApiChain | undefined;
  domain?: string;
  accountType?: AccountType;
  isSavedAddress?: boolean;
}

export interface NftTransfer {
  name?: string;
  address: string;
  thumbnail?: string;
  collectionName?: string;
}

export type GlobalState = {
  DEBUG_randomId?: number;

  appState: AppState;

  auth: {
    state: AuthState;
    method?: AuthMethod;
    isLoading?: boolean;
    mnemonic?: string[];
    mnemonicCheckIndexes?: number[];
    hardwareSelectedIndices?: number[];
    error?: string;
    pin?: string;
    isImportModalOpen?: boolean;
    accounts?: AuthAccount[];
    forceAddingTonOnlyAccount?: boolean;
    initialAddAccountState?: AccountSelectorState; // Initial rendering state for the `AccountSelectorModal` component
    shouldHideAddAccountBackButton?: boolean;
  };

  // Enclave
  authTypes?: AuthType[];
  enclaveSession?: EnclaveSession;
  /**
   * Accounts awaiting the multichain upgrade: they miss wallets for newly supported chains or a backend
   * auth token. The upgrade clears the list when it starts, so a non-empty value means it has not run yet.
   */
  multichainUpgradeAccountIds?: string[];

  biometrics: {
    state: BiometricsState;
    error?: string;
  };

  hardware: {
    hardwareState: HardwareConnectState;
    chain: ApiChain;
    /**
     * If true, `hardwareWallets` will be populated before the `hardwareState` switches to `Connected`.
     * This is slow, so use it only when necessary.
     */
    shouldLoadWallets?: true;
    /** All the wallets belong to `chain` */
    hardwareWallets?: ApiLedgerWalletInfo[];
    isLedgerConnected?: boolean;
    isChainAppConnected?: boolean;
    availableTransports?: LedgerTransport[];
    lastUsedTransport?: LedgerTransport;
    /** Loading flag for paginated wallet fetch (Show More) */
    isLoading?: boolean;
  };

  currentTransfer: {
    state: TransferState;
    isLoading?: boolean;
    // Should be ignored when `nfts` is defined and not empty
    tokenSlug: string;
    toAddress?: string;
    toAddressName?: string;
    resolvedAddress?: string;
    error?: string;
    /** The transaction draft check has rejected `toAddress`, which the address format check alone can't detect */
    toAddressError?: ApiAnyDisplayError;
    // Should be ignored when `nfts` is defined and not empty
    amount?: bigint;
    comment?: string;
    binPayload?: string;
    promiseId?: string;
    txId?: string;
    rawPayload?: string;
    stateInit?: string;
    shouldEncrypt?: boolean;
    isToNewAddress?: boolean;
    isScam?: boolean;
    nfts?: ApiNft[];
    sentNftsCount?: number;
    isMemoRequired?: boolean;
    // Every time this field value changes, the `amount` value should be actualized using `preserveMaxTransferAmount`
    diesel?: ApiFetchEstimateDieselResult;
    isGasless?: boolean;
    isGaslessWithStars?: boolean;
    scamWarningType?: ScamWarningType;
    isTransferReadonly?: boolean;
    isOfframp?: boolean;
    // Set for a transfer opened from a deeplink, a QR code or the agent rather than the app's own controls.
    // Such a transfer asks for the passcode even while the Remember Passcode window is open.
    shouldRequireFreshAuth?: boolean;
    isNftBurn?: boolean;
    /**
     * Normalized explanation of the fee and gasless parameters for the current draft, ready for UI consumption.
     * Calculated on the API layer inside chain-specific `checkTransactionDraft`.
     * Every time this field value changes, the `amount` value should be actualized using `preserveMaxTransferAmount`.
     */
    explainedFee?: ExplainedTransferFee;
    mfaRequestHash?: string;
  };

  currentSwap: {
    isMaxAmount?: boolean;
    state: SwapState;
    swapId?: string;
    slippage: number;
    route?: ApiSwapRouteType;
    tokenInSlug?: string;
    tokenOutSlug?: string;
    amountIn?: string;
    amountOut?: string;
    amountOutMin?: string;
    priceImpact?: number;
    activityId?: string;
    mfaRequestHash?: string;
    error?: string;
    errorType?: SwapErrorType;
    isLoading?: boolean;
    /**
     * When is `true`, does several things: shows the estimating indicator in the UI, blocks the form submission, and
     * instructs the UI and the actions to perform an estimation regardless.
     */
    isEstimating?: boolean;
    inputSource?: SwapInputSource;
    /**
     * The output the last estimate priced. When the user fixes the output, the form keeps their
     * figure and the estimate answers with what the venue could reach against it, so the two are
     * not the same number and only this one matches the routes the swap will be built from.
     */
    quotedAmountOut?: string;
    /** The address to send the "out" tokens to. Used only when the swap type is `CrosschainFromWallet`. */
    toAddress?: string;
    payinAddress?: string;
    payoutAddress?: string;
    payinExtraId?: string;
    isManualDepositRequired?: boolean;
    limits?: {
      fromMin?: string;
      fromMax?: string;
    };
    dieselStatus?: DieselStatus;
    dexLabel?: ApiSwapDexLabel;
    dexRouterLabel?: ApiSwapDexRouterLabel;
    /**
     * EVM only. The swap is sent as one batch user operation, so confirmation should warn that sending
     * can take longer. Set when a Permit2 approve, a router permit, or an Alchemy delegate is missing.
     */
    needsBatch?: boolean;
    routes?: ApiSwapRoute[][];
    currentCexLabel?: ApiSwapCexLabel;
    currentCexProviderName?: string;
    currentCexTermsOfUseUrl?: string;
    currentCexPrivacyPolicyUrl?: string;
    currentCexAmlKycPolicyUrl?: string;
    maxAmountFromBackend?: string;
    swapHint?: ApiSwapHint;
    /** Tester-only estimate fan-out summary from the backend `debug.summary` field. */
    estimateDebugSummary?: string;
    // Fees. Undefined values mean that these fields are unknown.
    networkFee?: string;
    realNetworkFee?: string;
    swapFee?: string;
    swapFeePercent?: number;
    ourFee?: string;
    ourFeePercent?: number;
    dieselFee?: string;
    /**
     * Set while the swap form runs as the token Buy / Sell screen. The screen token is `tokenOutSlug` when
     * buying and `tokenInSlug` when selling; the other side is the asset picked in "Pay With" / "You Receive".
     */
    tradeDirection?: TradeDirection;
    /** The fiat currency picked on the Buy / Sell screen instead of a counter token. The swap is not estimated then */
    tradeCardCurrency?: ApiBaseCurrency;
    /** The amount entered on the Buy / Sell screen, always in the screen token */
    tradeAmount?: string;
    /** Whether the Buy / Sell amount field shows token units rather than fiat */
    isTradeAmountInToken?: boolean;
  };

  currentSignature?: {
    promiseId: string;
    dataHex: string;
    error?: string;
    isSigned?: boolean;
  };

  exploreData?: {
    featuredTitle?: string;
    categories: ApiSiteCategory[];
    sites: ApiSite[];
  };

  // The showcase is localized by the backend, so it carries the language it was loaded in
  marketData?: ApiMarketAssetsResponseWithSlug & { langCode: LangCode };
  // The token screen opened from the market, as opposed to the one opened in the wallet
  marketTokenSlug?: string;

  currentDappTransfer: {
    state: TransferState;
    isSse?: boolean;
    // Set while this is a placeholder modal opened by a wake deeplink (no request yet)
    isWaitingForRequest?: boolean;
    returnUrl?: string;
    promiseId?: string;
    isLoading?: boolean;
    transactions?: ApiDappTransfer[];
    /** What else should happen after submitting the transactions (in addition to the transactions) */
    emulation?: Pick<ApiEmulationResult, 'activities' | 'realFee'>;
    /** Unix seconds */
    validUntil?: number;
    vestingAddress?: string;
    viewTransactionOnIdx?: number;
    dapp?: StoredDappConnection;
    operationChain?: ApiChain;
    error?: string;
    shouldHideTransfers?: boolean;
    // Deal with solana b58/b64 issues based on requested method
    isLegacyOutput?: boolean;
    mfaRequestHash?: string;
  };

  currentDappSignData: {
    state: SignDataState;
    isSse?: boolean;
    promiseId?: string;
    isLoading?: boolean;
    dapp?: StoredDappConnection;
    operationChain?: ApiChain;
    payloadToSign?: UnifiedSignDataPayload;
    parsedPayloadToSign?: ApiParsedSignDataCellPreview;
    error?: string;
  };

  currentWalletConnectPay: {
    state: WalletConnectPayState;
    operation?: 'transaction' | 'signData' | 'payment';
    promiseId?: string;
    accountId?: string;
    merchant?: WcPayMerchant;
    operationChain?: ApiChain;
    transactions?: ApiDappTransfer[];
    emulation?: Pick<ApiEmulationResult, 'activities' | 'realFee'>;
    paymentInfo?: WcPayPaymentInfo;
    paymentOption?: WcPayPaymentOption;
    isSignOnly?: boolean;
    isLegacyOutput?: boolean;
    shouldHideTransfers?: boolean;
    validUntil?: number;
    payloadToSign?: UnifiedSignDataPayload;
    containsApprove?: boolean;
    approveOperationChain?: ApiChain;
    approveTransactions?: ApiDappTransfer[];
    approveValidUntil?: number;
    txId?: string;
    paymentAmount?: WcPayPaymentAmount;
    isLoading?: boolean;
    error?: string;
  };

  walletConnectPayDataCollection?: {
    promiseId: string;
    url: string;
    isCompleting?: boolean;
  };

  walletConnectPayOptionSelection?: {
    promiseId: string;
    paymentLink: string;
    accountId: string;
    merchant: WcPayMerchant;
    paymentInfo?: WcPayPaymentInfo;
    options: WcPayPaymentOption[];
    isLoading?: boolean;
    shouldSwitchWallet?: boolean;
  };

  currentDomainRenewal: {
    addresses?: string[];
    state: DomainRenewalState;
    isLoading?: boolean;
    error?: string;
    // There's only one commission because the transaction has no change
    realFee?: bigint;
    txId?: string;
    mfaRequestHash?: string;
  };

  currentDomainLinking: {
    address?: string;
    state: DomainLinkingState;
    isLoading?: boolean;
    error?: string;
    realFee?: bigint;
    walletAddress?: string;
    walletAddressName?: string;
    resolvedWalletAddress?: string;
    txId?: string;
    mfaRequestHash?: string;
  };

  dappConnectRequest?: {
    state: DappConnectState;
    isSse?: boolean;
    isLoading?: boolean;
    /** True while a new wallet is being prepared after password OK (before auth safety flow) */
    isCreatingAccount?: boolean;
    /** Set after importMnemonic succeeds; used to resume dapp connect after backup flow */
    pendingConnectAccountId?: string;
    promiseId?: string;
    accountId?: string;
    dapp: StoredDappConnection;
    permissions?: ApiDappPermissions;
    proof?: TonConnectProof;
    error?: string;
    multichainResolution?: 'switched-account' | 'needs-new-wallet';
  };

  currentStaking: {
    state: StakingState;
    isLoading?: boolean;
    isUnstaking?: boolean;
    amount?: bigint;
    initialAmount?: bigint | 'all';
    // Paid out by the instant foreign unstake the stake form has returned from, so the form offers to restake it
    restakeAmount?: bigint;
    tokenAmount?: bigint;
    fee?: bigint;
    error?: string;
    mfaRequestHash?: string;
    foreignUnstake?: ForeignUnstake;
    // Lives until the staking modal closes, so dismissed banners come back the next time it opens
    dismissedBannerIds?: string[];
  };

  stakingDefault: ApiStakingState;

  accounts?: {
    byId: Record<string, Account>;
    isLoading?: boolean;
    error?: string;
  };

  tokenInfo: {
    bySlug: Record<string, ApiTokenWithPrice>;
  };

  currencyRates: ApiCurrencyRates;

  swapTokenInfo: {
    bySlug: Record<string, ApiSwapAsset>;
    /** Whether the API has loaded and provided the tokens */
    isLoaded?: true;
  };

  swapVersion: ApiSwapVersion;

  swapPairs?: {
    bySlug: Record<string, AssetPairs>;
  };

  tokenPriceHistory: {
    bySlug: Record<string, PriceHistoryPeriods>;
    restoredBySlug: Record<string, PriceHistoryPeriods>;
    // Comes with the session series and is not cached, as a restored series is shown without it
    backfillBySlug?: Record<string, PriceHistoryBackfillPeriods>;
  };

  tokenDetails: {
    bySlug: Record<string, TokenDetailsState>;
  };

  perpsAssets?: GlobalPerpsAssets;

  perpsAssetChart?: {
    byAssetId: Record<string, PriceHistoryPeriods>;
  };

  byAccountId: Record<string, AccountState>;

  walletVersions?: {
    currentVersion?: ApiTonWalletVersion;
    byId: Record<string, ApiWalletWithVersionInfo[]>;
  };

  settings: {
    state: SettingsState;
    theme: Theme;
    animationLevel: AnimationLevel;
    isSeasonalThemingDisabled?: boolean;
    is3dCardDisabled?: boolean;
    developerSettingsOverrides?: DeveloperSettingsOverrides;
    langCode: LangCode;
    langSource?: LanguageSource;
    byAccountId: Record<string, AccountSettings>;
    areTinyTransfersHidden?: boolean;
    areTokenNamesLocalized?: boolean;
    canPlaySounds?: boolean;
    isInvestorViewEnabled?: boolean;
    isTonProxyEnabled?: boolean;
    isDeeplinkHookEnabled?: boolean;
    isPasswordNumeric?: boolean; // Backwards compatibility for non-numeric passwords from older versions
    isTestnet?: boolean;
    isSecurityWarningHidden?: boolean;
    areTokensWithNoCostHidden: boolean;
    areUnverifiedNftsHidden?: boolean;
    areChainBadgesShown?: boolean;
    importToken?: {
      isLoading?: boolean;
      token?: UserToken | UserSwapToken;
      error?: string;
    };
    baseCurrency: ApiBaseCurrency;
    isAppLockEnabled?: boolean;
    autolockValue?: AutolockValueType;
    isAutoConfirmEnabled: boolean;
    isSensitiveDataHidden?: true;
    orderedAccountIds?: string[];
    selectedExplorerIds?: Partial<Record<ApiChain, string>>;
    installMfa?: {
      requestId: string;
      user?: {
        id: string;
        name: string;
        username?: string;
        avatarUrl?: string;
      };
      error?: string;
      /** Grows with every failed install attempt, so the screen can tell a new failure from the previous one */
      failedAttemptCount?: number;
    };

    removeMfa?: {
      requestId: string;
      error?: string;
    };
  };

  dialogs: DialogType[];
  toasts: ToastType[];
  currentAccountId?: string;
  currentTemporaryViewAccountId?: string;
  isAccountSelectorOpen?: boolean;
  walletRenameAccountId?: string;
  accountSelectorActiveTab?: number;
  accountSelectorViewMode?: 'cards' | 'list';
  isBackupWalletModalOpen?: boolean;
  isHardwareModalOpen?: boolean;
  isStakingInfoModalOpen?: boolean;
  isCustomizeWalletModalOpen?: boolean;
  customizeWalletReturnTo?: 'accountSelector' | 'settings';
  areSettingsOpen?: boolean;
  isAgentOpen?: boolean;
  isExploreOpen?: boolean;
  isMarketOpen?: boolean;
  isPortfolioOpen?: boolean;
  portfolioReturnTo?: 'settings';
  portfolio?: PortfolioState;
  isAppUpdateAvailable?: boolean;
  // Force show the "Update My Wallet" pop-up on all platforms
  isAppUpdateRequired?: boolean;
  appReloadReason?: AppReloadReason;
  seasonalTheme?: ApiBackendConfig['seasonalTheme'];
  isPromotionModalOpen?: boolean;
  confettiRequestedAt?: number;
  isPinAccepted?: boolean;
  onRampWidgetModal?: OnRampWidgetModalParams;
  offRampWidgetModal?: OffRampWidgetModalParams;
  isInvoiceModalOpen?: boolean;
  isReceiveModalOpen?: boolean;
  isVestingModalOpen?: boolean;
  isIncorrectTimeNotificationReceived?: boolean;
  isDerivationsSynced?: boolean;
  currentBrowserOptions?: {
    url: string;
    title?: string;
    subtitle?: string;
  };

  currentMintCard?: {
    type?: ApiMtwCardType;
    state?: MintCardState;
    error?: string;
    isLoading?: boolean;
    /** The `startsAt` we already polled the account config for after its countdown hit zero */
    checkedMintStartsAt?: number;
  };

  latestAppVersion?: string;
  stateVersion: number;
  isLegacyCoreMigrationCompleted?: true;
  restrictions: {
    isLimitedRegion: boolean;
    isSwapDisabled: boolean;
    isOnRampDisabled: boolean;
    isOffRampDisabled: boolean;
    isNftBuyingDisabled: boolean;
    isCopyStorageEnabled?: boolean;
    supportAccountsCount?: number;
    countryCode?: ApiCountryCode;
    allowedOnOffRampCurrencies?: ApiBaseCurrency[];
  };

  mediaViewer: {
    mediaId?: string;
    mediaType?: MediaType;
    txId?: string;
    hiddenNfts?: HiddenNftsSection;
    noGhostAnimation?: boolean;
    pictures?: MediaViewerPictures;
  };

  currentTransactionInfo: {
    state: TransactionInfoState;
    txId?: string;
    chain?: ApiChain;
    activities?: ApiActivity[];
    selectedActivityIndex?: number;
    error?: string;
  };

  isLoadingOverlayOpen?: boolean;

  pushNotifications: {
    isAvailable?: boolean;
    userToken?: string;
    platform?: NativePlatform;
    enabledAccounts: string[]; // Values - account ids
  };

  isAppLockActive?: boolean;
  isManualLockActive?: boolean;
  appLockHideBiometrics?: boolean;
  // The app is open in fullscreen mode in Telegram MiniApp on mobile
  isFullscreen?: boolean;
};

export interface ActionPayloads {
  // Initial
  init: undefined;
  initApi: undefined;
  afterInit: undefined;
  apiUpdate: ApiUpdate;
  resetAuth: undefined;
  startCreatingWallet: { enclaveToken?: string } | undefined;
  afterCheckMnemonic: undefined;
  afterCongratulations: { isImporting?: boolean };
  skipCheckMnemonic: undefined;
  restartCheckMnemonicIndexes: {
    wordsCount: number;
    preserveIndexes?: number[];
  };
  createPassword: { password: string; isNumeric?: boolean };
  skipBiometrics: undefined;
  setupBiometricAuth: undefined;
  skipCreateBiometrics: { isImporting?: boolean };
  createPin: { pin: string; isImporting: boolean };
  confirmPin: { isImporting: boolean };
  cancelConfirmPin: { isImporting: boolean };
  cancelCheckPassword: undefined;
  startImportingWallet: { enclaveToken?: string } | undefined;
  afterImportMnemonic: { mnemonic: string[] };
  startImportingHardwareWallet: { driver: ApiLedgerDriver };
  confirmDisclaimer: undefined;
  afterConfirmDisclaimer: undefined;
  cleanAuthError: undefined;
  openAbout: undefined;
  closeAbout: undefined;
  startImportViewAccount: undefined;
  closeImportViewAccount: undefined;
  openAuthImportWalletModal: undefined;
  closeAuthImportWalletModal: undefined;
  openAuthBackupWalletModal: undefined;
  openMnemonicPage: undefined;
  openCheckWordsPage: undefined;
  closeCheckWordsPage: { isBackupCreated?: boolean } | undefined;
  initializeHardwareWalletModal: undefined;
  initializeHardwareWalletConnection: { transport: LedgerTransport };
  createHardwareAccounts: undefined;
  addHardwareAccounts: { accounts: { accountId: string; byChain: Account['byChain'] }[] };
  loadMoreHardwareWallets: undefined;
  createAccount: { enclaveToken?: string; isPasswordNumeric?: boolean } | undefined;
  afterSelectHardwareWallets: { hardwareSelectedIndices: number[] };
  resetApiSettings: { areAllDisabled?: boolean } | undefined;
  checkAppVersion: undefined;
  checkAppBuild: undefined;
  importAccountByVersion: { version: ApiTonWalletVersion; isTestnetSubwalletId?: boolean };
  addSubWallet: { group: ApiGroupedWalletVariant };
  addAllFoundSubwallets: { foundSubwallets: ApiGroupedWalletVariant[] };
  createSubWallet: { enclaveToken: string };
  upgradeMultichainAccounts: { enclaveToken: string; accountIds: string[] };
  importViewAccount: { addressByChain: ApiImportAddressByChain };
  openTemporaryViewAccount: {
    addressByChain: Partial<Record<ApiChain, string>>;
    /** Opens the token screen in the new account. Allows `addressByChain` to be empty. */
    tokenSlug?: string;
  };
  saveTemporaryAccount: undefined;

  setEnclaveSession: EnclaveSession;
  releaseEnclaveSession: { enclaveToken: string };

  rollbackEnclaveMigration: undefined;
  migrateLegacyAuth: {
    password: string;
    isLongSession: boolean;
    usageCount?: number;
    onSuccess: (token: string) => void;
    onError: (error: MigrationErrorPresentation) => void;
  };
  migrateLegacyBiometricAuth: {
    legacyAuthConfig: LegacyAuthConfig;
    isLongSession: boolean;
    usageCount?: number;
    onSuccess: (token: string) => void;
    onError: (error: MigrationErrorPresentation) => void;
  };

  selectToken: { slug?: string } | undefined;
  openPerpsAsset: { assetId: string };
  closePerpsAsset: undefined;
  setCurrentPerpsAssetPeriod: { period: ApiPriceHistoryPeriod };
  openBackupWalletModal: undefined;
  closeBackupWalletModal: undefined;
  setIsBackupRequired: { isMnemonicChecked: boolean };
  openHardwareWalletModal: { chain: ApiChain };
  closeHardwareWalletModal: undefined;
  openCustomizeWalletModal: { returnTo?: 'accountSelector' | 'settings' };
  closeCustomizeWalletModal: undefined;
  resetHardwareWalletConnect: { chain: ApiChain; shouldLoadWallets?: boolean };
  setTransferScreen: { state: TransferState };
  setTransferAmount: { amount?: bigint };
  setTransferToAddress: { toAddress?: string };
  setTransferComment: { comment?: string };
  setTransferShouldEncrypt: { shouldEncrypt?: boolean };
  startTransfer: {
    tokenSlug?: string;
    amount?: bigint;
    toAddress?: string;
    comment?: string;
    nfts?: ApiNft[];
    binPayload?: string;
    stateInit?: string;
    isTransferReadonly?: boolean;
    isOfframp?: boolean;
    shouldRequireFreshAuth?: boolean;
  } | undefined;
  changeTransferToken: { tokenSlug: string; withResetAmount?: boolean };
  fetchTransferFee: {
    tokenSlug: string;
    toAddress: string;
    amount?: bigint;
    comment?: string;
    shouldEncrypt?: boolean;
    binPayload?: string;
    stateInit?: string;
  };
  fetchNftFee: {
    toAddress: string;
    nfts: ApiNft[];
    comment?: string;
  };
  submitTransferInitial: {
    tokenSlug: string;
    amount: bigint;
    toAddress: string;
    comment?: string;
    shouldEncrypt?: boolean;
    nfts?: ApiNft[];
    isGasless?: boolean;
    isBase64Data?: boolean;
    binPayload?: string;
    isGaslessWithStars?: boolean;
    stateInit?: string;
    isNftBurn?: boolean;
  };
  submitTransferConfirm: undefined;
  submitTransfer: { enclaveToken?: string } | undefined;
  updateMfaRequestStatus: undefined;
  clearTransferError: undefined;
  cancelTransfer: { shouldReset?: boolean } | undefined;
  switchTransferAccount: { accountId: string };
  showTransferScamWarning: { type: ScamWarningType };
  dismissTransferScamWarning: undefined;
  showDialog: DialogType;
  dismissDialog: undefined;
  showError: { error?: ApiAnyDisplayError | TeactNode | string };
  showToast: ToastType;
  dismissToast: undefined;
  initLedgerPage: undefined;
  afterSignIn: undefined;
  signOut: { level: SignOutLevel; accountId?: string };
  cancelCaching: undefined;
  afterSignOut: { shouldReset?: boolean } | undefined;
  addAccount: {
    method: AuthMethod;
    isAuthFlow?: boolean;
    clearDappConnectOnVerified?: boolean;
    /**
     * The token minted by the authorization that opened this flow. Carrying it forward is what tells the
     * flow it is already authorized - the session it came from is usage-scoped, so global state cannot
     * answer that question after the fact.
     */
    enclaveToken?: string;
  };
  addAccount2: { method: AuthMethod; enclaveToken?: string };
  switchAccount: { accountId: string; newNetwork?: ApiNetwork };
  renameAccount: { accountId: string; title: string };
  clearAccountError: undefined;
  clearAccountLoading: undefined;
  setIsAccountLoading: { isLoading: true | undefined };
  verifyHardwareAddress: { chain: ApiChain };
  authorizeDiesel: undefined;
  fetchTransferDieselState: { tokenSlug: string };
  setIsAuthLoading: { isLoading: true | undefined };

  fetchPastActivities: { accountId?: string; slug?: string; shouldLoadWithBudget?: boolean };
  showActivityInfo: { id: string };
  showAnyAccountTx: { txId: string; accountId: string; network: ApiNetwork; chain: ApiChain };
  showTokenActivity: { slug: string; returnTab?: ContentTab };
  closeTokenActivity: undefined;
  closeActivityInfo: { id: string };
  fetchActivityDetails: { id: string };

  // External transaction info (deeplink)
  openTransactionInfo:
    | { txId: string; chain: ApiChain; activities?: ApiActivity[] }
    | { txHash: string; chain: ApiChain; activities?: ApiActivity[] };
  closeTransactionInfo: undefined;
  selectTransactionInfoActivity: { index: number };
  fetchNftsFromCollection: { collection: ApiNftCollection };
  clearNftCollectionLoading: { collection: ApiNftCollection };
  openNftCollection: { chain: ApiChain; address: string };
  closeNftCollection: undefined;
  selectNfts: { nfts: ApiNft[] };
  selectAllNfts: { collectionAddress?: string };
  clearNftSelection: { address: string };
  clearNftsSelection: undefined;
  addCollectionTab: { collection: ApiNftCollection; isAuto?: boolean };
  removeCollectionTab: { collection: ApiNftCollection };
  burnNfts: { nfts: ApiNft[] };
  addNftsToBlacklist: { addresses: ApiNft['address'][] };
  addNftsToWhitelist: { addresses: ApiNft['address'][] };
  removeNftSpecialStatus: { address: ApiNft['address'] };
  openUnhideNftModal: {
    address: ApiNft['address'];
    name: ApiNft['name'];
  };
  closeUnhideNftModal: undefined;
  openHideNftModal: {
    addresses: ApiNft['address'][];
    isCollection: boolean;
  };
  closeHideNftModal: undefined;
  openReportNftModal: {
    chain: ApiChain;
    address: ApiNft['address'];
    shouldReport?: true;
  };
  closeReportNftModal: undefined;
  hideNft: { shouldReport?: true } | undefined;
  openNftAttributesModal: { nft: ApiNft; withOwner?: true };
  closeNftAttributesModal: undefined;

  openAgent: undefined;
  closeAgent: undefined;
  openExplore: undefined;
  closeExplore: undefined;
  openMarket: undefined;
  closeMarket: undefined;
  openMarketToken: { slug: string };
  openPortfolio: { returnTo?: 'settings' } | undefined;
  closePortfolio: undefined;
  loadPortfolioHistory: { range?: ApiPriceHistoryPeriod } | undefined;
  loadPortfolioPnlChange: undefined;

  closeAnyModal: undefined;
  submitSignature: { enclaveToken: string };
  clearSignatureError: undefined;
  cancelSignature: undefined;

  addSavedAddress: { address: string; name: string; chain: ApiChain };
  removeFromSavedAddress: { address: string; chain: ApiChain };
  checkTransferAddress: { address?: string; chain?: ApiChain };

  openAccountSelector: undefined;
  closeAccountSelector: undefined;
  openWalletRenameModal: { accountId?: string } | undefined;
  closeWalletRenameModal: undefined;
  setAccountSelectorTab: { tab: number };
  setAccountSelectorViewMode: { mode: 'cards' | 'list' };
  setCurrentTokenPeriod: { period: TokenPeriod };
  openAddAccountModal: {
    forceAddingTonOnlyAccount?: boolean;
    initialState?: AccountSelectorState;
    shouldHideBackButton?: boolean;
  } | undefined;
  closeAddAccountModal: undefined;

  setActiveContentTab: { tab: ContentTab };

  // BottomBar actions
  switchToWallet: undefined;
  switchToAgent: undefined;
  switchToExplore: undefined;
  switchToMarket: undefined;
  switchToSettings: undefined;
  switchToPortfolio: undefined;

  requestConfetti: undefined;
  setIsPinAccepted: undefined;
  clearIsPinAccepted: undefined;

  requestOpenQrScanner: undefined;
  handleQrCode: { data: string };

  // Staking
  startStaking: {
    stakingId?: string;
    tokenSlug?: string;
    initialAmount?: bigint | 'all';
  } | undefined;
  startUnstaking: { stakingId: string } | undefined;
  setStakingScreen: { state: StakingState };
  submitStakingInitial: { amount?: bigint; isUnstaking?: boolean } | undefined;
  submitStaking: { enclaveToken?: string; isUnstaking?: boolean } | undefined;
  clearStakingError: undefined;
  cancelStaking: undefined;
  fetchStakingHistory: undefined;
  fetchStakingFee: { amount: bigint };
  openStakingInfo: undefined;
  openAnyAccountStakingInfo: { accountId: string; network: ApiNetwork; stakingId: string };
  closeStakingInfo: undefined;
  changeCurrentStaking: { stakingId: string; shouldReopenModal?: boolean };
  startStakingClaim: { stakingId: string } | undefined;
  submitStakingClaim: { enclaveToken?: string } | undefined;
  cancelStakingClaim: undefined;
  openStakingInfoOrStart: undefined;
  updateStakingMfaRequestStatus: undefined;
  switchStakingAccount: { accountId: string; mode: 'stake' | 'unstake' | 'claim' };
  startForeignUnstake: { id: string };
  cancelForeignUnstake: undefined;
  submitForeignUnstake: { enclaveToken?: string } | undefined;
  dismissHomeBanner: { accountId: string; id: string };
  dismissStakingBanner: { id: string };

  // Settings
  openSettings: undefined;
  openSettingsWithState: { state: SettingsState };
  setSettingsState: { state?: SettingsState };
  closeSettings: undefined;
  setTheme: { theme: Theme };
  setAnimationLevel: { level: AnimationLevel };
  toggleSeasonalTheming: { isEnabled?: boolean };
  toggle3dCard: { isEnabled?: boolean };
  setDeveloperSettingsOverride: DeveloperSettingsOverridePayload;
  toggleTinyTransfersHidden: { isEnabled?: boolean } | undefined;
  toggleUnverifiedNftsHidden: { isEnabled?: boolean } | undefined;
  toggleLocalizedTokenNames: { isEnabled?: boolean } | undefined;
  toggleChainBadges: { isEnabled?: boolean } | undefined;
  toggleInvestorView: { isEnabled?: boolean } | undefined;
  toggleCanPlaySounds: { isEnabled?: boolean } | undefined;
  toggleTonProxy: { isEnabled: boolean };
  toggleDeeplinkHook: { isEnabled: boolean };
  startChangingNetwork: { network: ApiNetwork };
  changeNetwork: { network: ApiNetwork };
  changeLanguage: { langCode: LangCode };
  setSelectedExplorerId: { chain: ApiChain; explorerId: string };
  closeSecurityWarning: undefined;
  toggleTokensWithNoCost: { isEnabled: boolean };
  pinToken: { slug: string };
  unpinToken: { slug: string };
  toggleTokenVisibility: { slug: string; shouldShow: boolean };
  toggleChainVisibility: { chain: ApiChain; shouldShow: boolean };
  setChainDisplayMode: { displayMode: ChainDisplayMode };
  updateChainDisplayOrder: { orderedChains: ApiChain[] };
  setOverviewCellSize: { size: OverviewCellSize };
  setAreAssetsHidden: { isHidden: boolean };
  setAreCollectiblesHidden: { isHidden: boolean };
  addToken: { token: UserToken };
  deleteToken: { slug: string };
  importToken: { chain: ApiChain; address: string };
  updateOrderedAccountIds: { orderedAccountIds: string[] };
  rebuildOrderedAccountIds: undefined;
  resetImportToken: undefined;
  closeBiometricSettings: undefined;
  openBiometricsTurnOffWarning: undefined;
  enableBiometrics: { isLoginFlow?: boolean } | undefined;
  disableBiometrics: { newPassword?: string; isPasswordNumeric?: boolean; onError?: NoneToVoidFunction } | undefined;
  changePasscode: { passcode: string; onSuccess: NoneToVoidFunction };
  changeBaseCurrency: { currency: ApiBaseCurrency };
  copyStorageData: undefined;
  setAppLockValue: { value?: AutolockValueType; isEnabled: boolean };
  setIsManualLockActive: { isActive?: boolean; shouldHideBiometrics?: boolean };
  setIsAutoConfirmEnabled: { isEnabled: boolean };
  setIsAllowSuspiciousActions: { isEnabled: boolean };
  openSettingsHardwareWallet: undefined;
  apiUpdateWalletVersions: ApiUpdateWalletVersions;

  // tg2fa
  startMfaRecoveryProcess: { enclaveToken?: string };

  createInstallMfaRequest: undefined;
  updateInstallMfaRequest: undefined;
  clearMfaRequests: undefined;
  clearInstallMfaError: undefined;
  submitInstallMfa: { enclaveToken?: string };

  updateRemoveMfaRequest: undefined;
  submitRemoveMfa: { enclaveToken?: string };
  clearRemoveMfaError: undefined;

  // Account Settings
  setCardBackgroundNft: { nft: ApiNft; accountId?: string };
  clearCardBackgroundNft: undefined;
  checkCardNftOwnership: { accountId: string } | undefined;
  installAccentColorFromNft: { nft: ApiNft; accountId?: string };
  clearAccentColorFromNft: undefined;

  // TON Connect common
  apiUpdateDappLoading: ApiUpdateDappLoading;
  apiUpdateDappCloseLoading: ApiUpdateDappCloseLoading;

  // TON Connect connection
  submitDappConnectRequestConfirm: { accountId: string; enclaveToken?: string };
  clearDappConnectRequestError: undefined;
  cancelDappConnectRequestConfirm: { promiseId?: string } | undefined;
  setDappConnectRequestState: { state: DappConnectState };
  apiUpdateDappConnect: ApiUpdateDappConnect;

  // TON Connect transfer
  setDappTransferScreen: { state: TransferState };
  showDappTransferTransaction: { transactionIdx: number };
  submitDappTransferConfirm: undefined;
  submitDappTransfer: { enclaveToken?: string } | undefined;
  clearDappTransferError: undefined;
  cancelDappTransfer: { promiseId?: string } | undefined;
  closeDappTransfer: undefined;
  apiUpdateDappSendTransaction: ApiUpdateDappSendTransactions;

  // TON Connect SignData
  setDappSignDataScreen: { state: SignDataState };
  submitDappSignDataConfirm: undefined;
  submitDappSignData: { enclaveToken?: string } | undefined;
  clearDappSignDataError: undefined;
  cancelDappSignData: { promiseId?: string } | undefined;
  closeDappSignData: undefined;
  apiUpdateDappSignData: ApiUpdateDappSignData;

  getDapps: undefined;
  deleteAllDapps: undefined;
  deleteDapp: { url: string; uniqueId: string };
  loadExploreSites: { isLandscape: boolean; langCode: LangCode | undefined };
  loadMarketAssets: undefined;
  updateDappLastOpenedAt: { url: string };
  updateDappMfaRequestStatus: undefined;

  addSiteToBrowserHistory: { url: string };
  removeSiteFromBrowserHistory: { url: string };
  openBrowser: { url: string; title?: string; subtitle?: string };
  closeBrowser: undefined;
  openSiteCategory: { id: number };
  closeSiteCategory: undefined;
  switchAccountAndOpenUrl: {
    accountId?: string;
    network?: ApiNetwork;
    url: string;
    isExternal?: boolean;
    title?: string;
    subtitle?: string;
  };

  // Swap
  submitSwap: { enclaveToken: string };
  startTokenTrade: { tokenSlug: string; direction: TradeDirection };
  startTokenSwap: { tokenSlug: string };
  /**
   * `amount` is in the screen token unless `isAmountIn` says it is the paying amount, which only differs
   * when buying. `isMaxAmount` marks the whole balance so the backend subtracts the fee itself.
   */
  setTradeAmount: { amount?: string; isMaxAmount?: boolean; isAmountIn?: boolean };
  setTradeCardCurrency: { currency?: ApiBaseCurrency };
  setTradeAmountUnit: { isInToken: boolean };
  startSwap: {
    state?: SwapState;
    tokenInSlug?: string;
    tokenOutSlug?: string;
    amountIn?: string;
    toAddress?: string;
  } | undefined;
  cancelSwap: { shouldReset?: boolean } | undefined;
  switchSwapAccount: { accountId: string };
  setDefaultSwapParams: { withResetAmount?: boolean } | undefined;
  switchSwapTokens: undefined;
  setSwapTokenIn: { tokenSlug: string };
  setSwapTokenOut: { tokenSlug: string };
  setSwapAmountIn: { amount?: string; isMaxAmount?: boolean };
  setSwapAmountOut: { amount?: string };
  setSlippage: { slippage: number };
  estimateSwap: undefined;
  setSwapScreen: { state: SwapState };
  clearSwapError: undefined;
  submitSwapCex: { enclaveToken: string };
  updateSwapMfaRequestStatus: undefined;
  setSwapCexAddress: { toAddress: string };
  addSwapToken: { token: UserSwapToken };
  updatePendingSwaps: { forceProviderRefresh?: boolean; contextActivities?: ApiActivity[] } | undefined;

  openOnRampWidgetModal: OnRampWidgetModalParams;
  closeOnRampWidgetModal: undefined;

  /** Without params, the widget sells on the chain of the last transferred token, see `selectDefaultOffRampChain` */
  openOffRampWidgetModal: OffRampWidgetModalParams | undefined;
  closeOffRampWidgetModal: undefined;

  // WalletConnect Pay
  apiUpdateWalletConnectPayLoading: { accountId: string };
  apiUpdateWalletConnectPayProcessing: ApiUpdateWalletConnectPayProcessing;
  apiUpdateWalletConnectPayPaymentComplete: ApiUpdateWalletConnectPayPaymentComplete;
  apiUpdateWalletConnectPaySignTransaction: ApiUpdateWalletConnectPaySignTransaction;
  apiUpdateWalletConnectPaySignData: ApiUpdateWalletConnectPaySignData;
  submitWalletConnectPaySignTransaction: { enclaveToken?: string } | undefined;
  submitWalletConnectPaySignData: { enclaveToken?: string } | undefined;
  clearWalletConnectPayError: undefined;
  cancelWalletConnectPay: undefined;
  closeWalletConnectPay: undefined;
  completeWalletConnectPayDataCollection: undefined;
  closeWalletConnectPayDataCollection: undefined;
  confirmWalletConnectPayOptionSelection: { optionId: string };
  closeWalletConnectPayOptionSelection: undefined;
  switchWalletConnectPayOptionSelectionAccount: { accountId: string };

  // MediaViewer
  openMediaViewer: {
    mediaId: string;
    mediaType: MediaType;
    txId?: string;
    hiddenNfts?: HiddenNftsSection;
    noGhostAnimation?: boolean;
    /** The pictures of a viewer other than the NFTs', which it keeps while it pages through them */
    pictures?: MediaViewerPictures;
  };
  closeMediaViewer: undefined;

  openReceiveModal: { chain: ApiChain } | undefined;
  closeReceiveModal: undefined;
  setReceiveActiveTab: { chain: ApiChain };
  openInvoiceModal: { tokenSlug: string } | undefined;
  changeInvoiceToken: { tokenSlug: string };
  closeInvoiceModal: undefined;

  loadPriceHistory: { slug: string; period: ApiPriceHistoryPeriod; currency?: ApiBaseCurrency };
  loadTokenNetWorthHistory: {
    slug: string;
    period: ApiPriceHistoryPeriod;
    currency?: ApiBaseCurrency;
  };
  loadTokenDetails: { slugs: string[] };
  preloadTokenDetails: undefined;
  loadPerpsAsset: { assetId: string };
  loadPerpsAssetChart: { assetId: string; period: ApiPriceHistoryPeriod };

  showIncorrectTimeError: undefined;

  openLoadingOverlay: undefined;
  closeLoadingOverlay: undefined;

  loadMycoin: undefined;
  openVestingModal: undefined;
  closeVestingModal: undefined;
  startClaimingVesting: undefined;
  submitClaimingVesting: { enclaveToken?: string } | undefined;
  clearVestingError: undefined;
  cancelClaimingVesting: undefined;

  openMintCardModal: undefined;
  closeMintCardModal: undefined;
  openPromotionModal: undefined;
  closePromotionModal: undefined;
  startCardMinting: { type: ApiMtwCardType };
  submitMintCard: { enclaveToken?: string } | undefined;
  clearMintCardError: undefined;
  checkMintStart: { startsAt: number };

  toggleNotifications: { isEnabled: boolean };
  renameNotificationAccount: { accountId: string };
  toggleNotificationAccount: { accountId: string };
  createNotificationAccount: { accountId: string; withAbort?: boolean };
  tryAddNotificationAccount: { accountId: string };
  deleteNotificationAccount: { accountId: string; withAbort?: boolean };
  deleteAllNotificationAccounts: undefined | { accountIds: string[] };
  registerNotifications: { userToken: string; platform: NativePlatform };

  openFullscreen: undefined;
  closeFullscreen: undefined;
  setAppLayout: { layout: AppLayout };

  setIsSensitiveDataHidden: { isHidden: boolean };

  openDomainRenewalModal: { accountId?: string; network?: ApiNetwork; addresses: string[] };
  startDomainsRenewal: undefined;
  checkDomainsRenewalDraft: { nfts: ApiNft[] };
  submitDomainsRenewal: { enclaveToken?: string } | undefined;
  clearDomainsRenewalError: undefined;
  cancelDomainsRenewal: undefined;
  updateDomainsRenewalMfaRequestStatus: undefined;

  openDomainLinkingModal: { address: string };
  startDomainLinking: undefined;
  checkDomainLinkingDraft: { nft: ApiNft };
  submitDomainLinking: { enclaveToken?: string } | undefined;
  clearDomainLinkingError: undefined;
  cancelDomainLinking: undefined;
  updateDomainLinkingMfaRequestStatus: undefined;

  checkLinkingAddress: { address?: string };
  setDomainLinkingWalletAddress: { address?: string };
  setIsAppLockActive: { isActive: boolean };
}

export enum LoadMoreDirection {
  Forwards,
  Backwards,
}

export type AutolockValueType = (typeof AUTOLOCK_OPTIONS_LIST[number])['value'];
